import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {isUserAccountsSession, type AuthTokenClaims} from "shared/utils/userAccountSession";

type TokenPayload = AuthTokenClaims;

const AUTH_BUFFER_SECONDS = 300;
const CHECK_INTERVAL_MS = 20_000;

function readToken() { return localStorage.getItem("authToken"); }
function isAuthorizedNow(): boolean {
    const token = readToken();
    if (!token) return false;
    try {
        const d = jwtDecode<TokenPayload>(token);
        const now = Math.floor(Date.now() / 1000);
        const valid = (d.exp ?? 0) - AUTH_BUFFER_SECONDS > now;
        return Boolean(valid) && isUserAccountsSession(d);
    } catch {
        return false;
    }
}

const ProtectedRoute: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const id = setInterval(() => {
            if (!isAuthorizedNow()) {
                navigate("/", { replace: true });
            }
        }, CHECK_INTERVAL_MS);

        const onStorage = (e: StorageEvent) => {
            if (e.key === "authToken" && !isAuthorizedNow()) {
                navigate("/", { replace: true });
            }
        };
        window.addEventListener("storage", onStorage);

        return () => {
            clearInterval(id);
            window.removeEventListener("storage", onStorage);
        };
    }, [navigate]);

    const authorized = isAuthorizedNow();
    return authorized ? <Outlet /> : <Navigate to="/login" replace state={{ from: location }} />;
};

export default ProtectedRoute;
